import React, { useEffect, useState } from 'react';
import { Mic } from 'lucide-react';
import { prefersReducedMotion, useInView } from './useInView';
import { ISOTIPO_VERDE } from './brand';

// Demo animada del asistente: nota de voz grabada sin señal (KAN-225, se envía
// al reconectar) → escribiendo… → respuesta con los datos del rodeo. Datos de
// ejemplo. Arranca al entrar en pantalla; con prefers-reduced-motion se muestra
// la conversación completa, sin animar.

const WAVE = [6, 12, 18, 10, 20, 14, 8, 16, 22, 12, 6, 14, 18, 9, 5, 11, 16, 8];
type Fase = 0 | 1 | 2 | 3; // 0 vacío · 1 pregunta · 2 escribiendo · 3 respuesta

const AssistantDemo: React.FC = () => {
  const [ref, inView] = useInView<HTMLDivElement>();
  const [fase, setFase] = useState<Fase>(3);

  useEffect(() => {
    if (!inView || prefersReducedMotion()) return;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const seq = () => {
      setFase(0);
      timers.push(setTimeout(() => setFase(1), 400));
      timers.push(setTimeout(() => setFase(2), 1300));
      timers.push(setTimeout(() => setFase(3), 2900));
      timers.push(setTimeout(seq, 10000));
    };
    seq();
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  return (
    <div ref={ref}>
      <div className="lp-chat" aria-label="Ejemplo de conversación con el asistente">
        <div className="lp-chat__h">
          <img src={ISOTIPO_VERDE} alt="" />
          <div>
            <b>Asistente MiCampoDigital</b>
            <small>Conoce Estancia La Primavera</small>
          </div>
        </div>
        <div className="lp-msgs">
          <div className={`lp-m lp-m--u ${fase >= 1 ? 'on' : ''}`}>
            <div className="lp-voice">
              <span className="lp-voice__play">
                <svg viewBox="0 0 10 12"><path d="M0 0l10 6-10 6z" /></svg>
              </span>
              <span className="lp-wave" aria-hidden="true">
                {WAVE.map((h, i) => (
                  <i key={i} style={{ height: h }} />
                ))}
              </span>
              <span>0:06</span>
            </div>
            <span className="lp-transcript">“¿Cuántas vacas vacías me quedaron en el Norte?”</span>
            <span className="lp-pending">
              <i />
              Grabada sin señal · enviada al volver
            </span>
          </div>
          {fase === 2 && (
            <div className="lp-m lp-m--b on">
              <span className="lp-typing" aria-label="Escribiendo">
                <i />
                <i />
                <i />
              </span>
            </div>
          )}
          <div className={`lp-m lp-m--b ${fase === 3 ? 'on' : ''}`}>
            <div className="lp-big">
              <b>23</b>
              <span>vacas vacías en Cuadro Norte · 18 % del lote</span>
            </div>
            Estas 3 fallaron los dos últimos tactos:
            <div className="lp-cows">
              <div className="lp-cow"><b>EO 4790</b><span>2 de 2 vacía</span></div>
              <div className="lp-cow"><b>EO 4512</b><span>2 de 3 vacía</span></div>
              <div className="lp-cow"><b>RO 1088</b><span>2 de 3 vacía</span></div>
            </div>
            <span className="lp-act">Ver la selección →</span>
          </div>
        </div>
        <div className="lp-input">
          <span>Escribí tu pregunta…</span>
          <span className="lp-mic"><Mic /></span>
        </div>
        <p className="lp-hint">Mantené apretado el micrófono para hablar</p>
      </div>
      <p className="lp-note lp-cap">Datos de ejemplo.</p>
    </div>
  );
};

export default AssistantDemo;
